import { createGlobalStyle } from "styled-components";
import { pxToRem } from "./utils";

export const GlobalStyles = createGlobalStyle`
  :root {
    --real-vh: 100vh;
    --keyboard-height: 0px;
    --keyboard-visible: 0;
    --ui-scale: 0.8;
    --font-family: "Golos Text Variable", sans-serif;
  }

  /*
    Масштаб макета подстраивается под окно: вёрстка не перестраивается,
    меняется только корневой font-size, от которого считается весь pxToRem.
    Пороги по высоте так же важны, как по ширине, потому что страницы
    со shared Table рассчитаны на height: 100% без внешнего скролла.
  */
  @media (max-width: 1500px), (max-height: 860px) {
    :root {
      --ui-scale: 0.76;
    }
  }

  @media (max-width: 1320px), (max-height: 760px) {
    :root {
      --ui-scale: 0.72;
    }
  }

  @media (max-width: 1160px), (max-height: 680px) {
    :root {
      --ui-scale: 0.68;
    }
  }

  @media (max-width: 980px), (max-height: 610px) {
    :root {
      --ui-scale: 0.64;
    }
  }

  @media (max-width: 860px), (max-height: 560px) {
    :root {
      --ui-scale: 0.6;
    }
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  html {
    font-size: calc(62.5% * var(--ui-scale));
    height: 100%;
  }

  html, body {
    font-family: "Golos Text Variable", sans-serif;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    scrollbar-width: none;
    height: 100%;
    overflow-x: hidden;
  }

  button,
  input,
  textarea,
  select {
    font-family: inherit;
  }

  input,
  textarea,
  select {
    --autofill-background: ${({ theme }) => theme.colors.white};
    --autofill-text-color: ${({ theme }) => theme.colors.ink};
  }

  /*
    Chromium-based browsers paint their own blue/yellow autofill surface with
    an internal !important background. Cover that browser paint with the
    field's own surface instead of hardcoding one background for every form.
    Components with a non-white surface can override --autofill-background.
  */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:active,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:active,
  select:-webkit-autofill,
  select:-webkit-autofill:hover,
  select:-webkit-autofill:active {
    -webkit-text-fill-color: var(--autofill-text-color) !important;
    caret-color: var(--autofill-text-color);
    -webkit-box-shadow: 0 0 0 ${pxToRem(2000)} var(--autofill-background) inset !important;
  }

  input:-webkit-autofill:focus,
  textarea:-webkit-autofill:focus,
  select:-webkit-autofill:focus {
    -webkit-text-fill-color: var(--autofill-text-color) !important;
    caret-color: var(--autofill-text-color);
    -webkit-box-shadow:
      0 0 0 ${pxToRem(2000)} var(--autofill-background) inset,
      0 0 0 ${pxToRem(2)} ${({ theme }) => theme.colors.brandMedium} !important;
  }

  #root {
    scrollbar-width: none;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .p-toast {
    min-width: ${pxToRem(350)} !important;
    font-size: ${pxToRem(16)} !important;
  }

  .p-toast-message {
    font-size: ${pxToRem(16)} !important;
  }

  .p-toast-detail {
    white-space: pre-line;
  }

  .p-datepicker.p-component.custom-calendar-panel.p-datepicker-monthpicker.p-ripple-disabled {
    font-size: ${pxToRem(10)};
    margin-left: ${pxToRem(50)};
    width: ${pxToRem(250)} !important;
  }

  .p-datepicker.p-component.custom-calendar-panel.p-datepicker-monthpicker.p-ripple-disabled.p-connected-overlay-enter {
    font-size: ${pxToRem(10)};
    margin-left: ${pxToRem(50)};
    width: ${pxToRem(250)} !important;
  }

  .p-datepicker.p-component.custom-calendar-panel.p-datepicker-monthpicker.p-ripple-disabled.p-connected-overlay-enter-done {
    font-size: ${pxToRem(10)};
    margin-left: ${pxToRem(50)};
    width: ${pxToRem(250)} !important;
  }

  .p-datepicker {
    width: ${pxToRem(250)};
    height: ${pxToRem(280)};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    margin-left: ${pxToRem(50)};
  }

  .p-datepicker.p-datepicker-monthpicker {
    width: ${pxToRem(250)};
    height: ${pxToRem(170)};
  }

  .p-datepicker-calendar {
    width: 100%;
    table-layout: fixed;
  }

  .p-datepicker-calendar td,
  .p-datepicker-calendar th {
    height: ${pxToRem(5)};
    width: ${pxToRem(5)};
    padding: 0;
    text-align: center;
    vertical-align: middle;
  }

  .p-datepicker-calendar tr {
    line-height: 1;
    margin: 0;
  }

  .p-datepicker-calendar td span {
    padding: 0;
    font-size: ${pxToRem(10)};
    line-height: 1.2;
  }

  .p-datepicker-header {
    padding: ${pxToRem(4)} 0;
    text-align: center;
    font-size: ${pxToRem(14)};
  }

  .p-datepicker-header button {
    width: ${pxToRem(24)};
    height: ${pxToRem(24)};
    padding: 0;
  }

  .p-datepicker-prev-icon,
  .p-datepicker-next-icon {
    width: ${pxToRem(14)};
    height: ${pxToRem(14)};
  }

  .p-datepicker-calendar thead th span {
    font-size: ${pxToRem(10)};
    line-height: 1;
    text-align: center;
  }

  .p-datepicker-title {
    margin-left: ${pxToRem(30)};
    margin-right: ${pxToRem(30)};
    gap: ${pxToRem(40)};
    display: flex;
    justify-content: space-between;
  }
`;

export default GlobalStyles;
